import { Box, Skeleton, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import userBannersStyles from "./UserBanners.styles";
import type { BannerItem } from "./UserBannersService";
import { getBanners } from "./UserBannersService";

const UserBanners = () => {
  const classes = userBannersStyles;
  const { t } = useTranslation();
  const [banners, setBanners] = useState<BannerItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadBanners = useCallback(async () => {
    setLoading(true);
    const items = await getBanners();
    setBanners(items);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const handleBannerClick = (link: string) => {
    const trimmed = link?.trim();
    if (!trimmed) return;
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      window.open(trimmed, "_blank", "noopener,noreferrer");
    } else {
      window.open(`https://${trimmed}`, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <Box sx={classes.root}>
        <Box sx={classes.grid}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={180}
              sx={{
                borderRadius: "16px",
                bgcolor: "rgba(255,255,255,0.04)",
                boxShadow: "none",
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (banners.length === 0) {
    return <Box sx={classes.emptyBox} />;
  }

  const count = banners.length;
  const gridStyle = count < 4 ? classes.gridCentered : classes.grid;
  const cardExtraStyle = count < 4 ? classes.bannerCardCentered : {};

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={gridStyle}>
        {banners.map((banner) => (
          <Box
            key={banner.index}
            component="button"
            type="button"
            sx={[classes.bannerCard, cardExtraStyle]}
            onClick={() => handleBannerClick(banner.link)}
            aria-label={t("openBannerLink", "Open link")}
          >
            {/* Blurred background — same image scaled+blurred to fill card */}
            <Box
              sx={[
                classes.bannerBgBlur,
                { backgroundImage: `url(${banner.image})` },
              ]}
            />
            <Box sx={classes.bannerImageWrap}>
              <img
                src={banner.image}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
              <Box sx={classes.bannerOverlay} />
              <Box className="banner-cta-pill" sx={classes.bannerCtaWrap}>
                <Box sx={classes.bannerCtaPillGradient} component="span">
                  <Typography component="span" sx={classes.bannerCtaArrow}>
                    →
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default UserBanners;
